import store from "@/store";
import EventService from "@/services/Event";
import Message from "@/domain/metrics/message";

/**
 * Legacy redirects for specific clients
 */
function handleLegacyRedirects(to) {
    const ps = to.query.ps
    const hostname = location.hostname

    if (ps === '2592' && hostname.includes('www.')) {
        location.replace(location.href.replace('www.', 'watchesandwonders.'))
        return true
    }
    if (ps === '2684' && hostname.includes('witevents.') && location.href.includes('JAPANOR24')) {
        location.replace(location.href.replace(/JAPANOR24-[A-Z0-9-]+/, "WITJPNA24-479AA2126-CTBOUAPST"))
        return true
    }
    if (ps === '2620' && hostname.includes('camparigroup.')) {
        location.replace(location.href.replace('camparigroup.', 'campari.').replace('2620', '2756').replace(/BARTCOM24-[A-Z0-9-]+/, "BARCO2024-2D988BF0F-JY0TH7MAM"))
        return true
    }
    if (ps === '2921') {
        location.replace(location.href.replace(/CAMPARI77-[A-Z0-9-]+/, 'LOCARNO77-DD6291249-5ZS48KG8V').replace('2921', '2916'))
        return true
    }
    return false
}

/**
 * Check if query has valid preview parameters
 */
function hasValidPreviewParams(query) {
    const hasEncryptedParams = query.ep || query.pc
    const hasPicaService = query.ps || query.pica_service
    const hasPicacode = query.picacode
    return (hasEncryptedParams && hasPicaService) || hasPicacode
}

/**
 * Save preview state to store
 */
function savePreviewState(data, query) {
    store.commit('SET_AUTO_CHECKIN_PICACODE', data.picacode)
    store.commit('SET_IS_PREVIEW_CHECKIN', true)
    store.commit('SET_AUTO_CHECKIN_SOURCE', data.source)
    store.commit('SET_AUTO_CHECKIN_TAGS', data.tags)
    store.commit('SET_AUTO_CHECKIN_SHOW_MARKETING_CHECK', query.ps === '2394')
    store.commit('SET_DISABLE_SOCIAL_LOGIN_IN_PREVIEW', data.disable_social_login)
    store.commit('SET_DATE_OF_STAY', { start: data.start_of_stay, end: data.end_of_stay })

    if (query.redirect) {
        store.commit('SET_REDIRECT_TO', query.redirect)
    }

    if (data.campaign_code) {
        const campaign = {
            campaign_code: data.campaign_code,
            campaign_expires_at: data.campaign_expires_at
        }
        const campaignJson = JSON.stringify(campaign)
        localStorage.setItem("campaign", campaignJson)
        store.commit('cartDigitalItems/setCampaign', campaignJson)
    }

    if (data.email) {
        store.commit('auth/SET_CONTEXT_PARAMS', {
            email: data.email,
            picacode: data.picacode
        })
    }
}

/**
 * Handle authentication using auth object from backend.
 * The auth.type matches AuthProviders constants (jwt, sso, ibi).
 * Delegates to the appropriate provider via store.
 */
async function handleAuth(auth) {
    if (!auth || !auth.type) return false

    // IBI requires special pre-auth setup
    if (auth.type === 'ibi') {
        if (window.cookiehub) {
            window.cookiehub.allowAll()
            window.cookiehub.closeDialog()
        }
        localStorage.setItem("app_env", 'webview')
        store.commit('appConfig/SET_APP_ENV', 'webview')
    }

    try {
        // auth.type is already the provider method (jwt, sso, ibi)
        // Pass the entire auth object as credentials - provider knows what it needs
        await store.dispatch('auth/authenticate', {
            method: auth.type,
            credentials: auth,
            remember: true
        })
        return true
    } catch (error) {
        console.error(`Auth failed for type ${auth.type}:`, error)
        return false
    }
}

/**
 * Build preview page params
 */
function buildPreviewParams(data, extra = {}) {
    return {
        name: 'preview',
        params: {
            photos: data.photos,
            event_name: data.event_name,
            experience_type: data.experience_type,
            ...extra
        }
    }
}

export default [
    {
        path: '/preview',
        name: 'preview',
        component: () => import(/* webpackChunkName: "preview" */ '../views/PreviewPage.vue'),
        props: true,
        beforeEnter: async (to, from, next) => {
            // 1. Handle legacy redirects
            if (handleLegacyRedirects(to)) return

            // 2. Already processed (has photos in params)
            if (to.params.photos) return next()

            // 3. Missing required params - redirect away
            if (!hasValidPreviewParams(to.query)) {
                return next({ name: store.getters['auth/isAuthenticated'] ? 'checkins-list' : 'homepage' })
            }

            Message.openedCheckinPreview(to.query)

            // 4. Fetch preview data
            try {
                const response = await EventService.getPreviewPhotos(to.query)
                const data = response.data

                // Save state first (before any auth/redirect)
                savePreviewState(data, to.query)

                const isAuthenticated = store.getters['auth/isAuthenticated']
                const currentUserId = store.state.auth.user && store.state.auth.user.id

                // 5. Check user mismatch (logged in with wrong user)
                if (data.user_id && isAuthenticated && currentUserId !== data.user_id) {
                    return next(buildPreviewParams(data, {
                        emailMismatch: true,
                        pendingAuth: data.auth  // Pass auth for "switch account" flow
                    }))
                }

                // 6. Already logged in with correct user
                if (data.user_id && isAuthenticated && currentUserId === data.user_id) {
                    return next({ name: 'checkins-list' })
                }

                // 7. Handle auth from backend response
                if (data.auth) {
                    const authHandled = await handleAuth(data.auth)
                    if (authHandled) return next({ name: 'checkins-list' })
                }

                // 8. Handle IBI auth from URL query param (legacy)
                if (to.query.accessToken) {
                    const authHandled = await handleAuth({ type: 'ibi', token: to.query.accessToken })
                    if (authHandled) return next({ name: 'checkins-list' })
                }

                // 9. Not logged in, no auto-auth - show preview page
                return next(buildPreviewParams(data))

            } catch (error) {
                return next({ name: 'checkin-error', params: { error } })
            }
        },
    },
]
